import React, { useEffect, useRef, useState } from "react";
import { SectionHeader } from "../Headlines";
import './index.scss'
import { useBreakpointContext } from "../../util/BreakpointProvider";

// This is copied from precompile-data.scss
const mobileHeaderHeight = 62;
const tabletHeaderHeight = 88;

const MOBILE_ASPECT_RATIO = 2 / 3;
const TABLET_ASPECT_RATIO = 3 / 2;

// Start means percentage of local progress
// End means percentage of progress into the NEXT caption
const CAPTION_START_TIME = 0.45;
const CAPTION_END_TIME = 0.2;

// Easing function for smooth transitions
const easeOutQuint = (x) => 1 - Math.pow(1 - x, 4);
const lerp = (start, end, t) => start + (end - start) * t;

const calculateScrollProgress = (element) => {
  const rect = element.getBoundingClientRect();
  const scrollOffset = Math.max(0, window.innerHeight - rect.top);
  return Math.min(1, Math.max(0, scrollOffset / rect.height));
};

const calculateCaptionIndices = (progress, captionsLength) => {
  const scaledProgress = progress * captionsLength;
  // if we're lerping towards the first caption, current index is -1, not 0.
  const current = Math.floor(scaledProgress) - 1;
  const next = current + 1;
  const localProgress = scaledProgress % 1;
  const displayCaption = localProgress <= CAPTION_END_TIME ? current : localProgress >= CAPTION_START_TIME ? next : null;
  return {
    current,
    next,
    displayCaption,
    localProgress,
  };
};

const calculateImageTransform = (progress, captions, { current, next, localProgress }) => {
  const INITIAL_STATE = { zoom: 1, x: 50, y: 50 };

  // Determine "from" state
  const fromState = current === -1 ? INITIAL_STATE : captions[current];

  // Determine "to" state
  const toState = next >= captions.length ? captions[current] : captions[next];


  const easedProgress = easeOutQuint(localProgress);

  const zoom = lerp(fromState.zoom, toState.zoom, easedProgress);
  const x = lerp(fromState.x, toState.x, easedProgress);
  const y = lerp(fromState.y, toState.y, easedProgress);

  const scale = 1 + (zoom - 1) * progress;
  return { scale, transformOrigin: `${x}% ${y}%` };
};

const getImageDimensions = (isMobile, isTablet) => {
  const containerWidth = Math.min(1440, window.innerWidth) - 48;
  const aspectRatio = isMobile || isTablet ? MOBILE_ASPECT_RATIO : TABLET_ASPECT_RATIO;
  
  // Calculate available height (viewport minus header and any other UI elements)
  const headerHeight = isMobile || isTablet ? 62 : 88;
  const availableHeight = window.innerHeight - headerHeight;
  
  // Calculate dimensions based on width constraint
  const widthConstrainedWidth = containerWidth;
  const widthConstrainedHeight = widthConstrainedWidth / aspectRatio;
  
  // Calculate dimensions based on height constraint
  const heightConstrainedHeight = availableHeight;
  const heightConstrainedWidth = heightConstrainedHeight * aspectRatio;
  
  // Choose the constraint that results in smaller dimensions (fits in both width and height)
  let imageWidth, imageHeight;
  
  if (widthConstrainedHeight <= availableHeight) {
    // Width constraint is the limiting factor
    imageWidth = widthConstrainedWidth;
    imageHeight = widthConstrainedHeight;
  } else {
    // Height constraint is the limiting factor
    imageWidth = heightConstrainedWidth;
    imageHeight = heightConstrainedHeight;
  }
  
  return { imageWidth, imageHeight };
}


export function IdentifyingCuttlefish({ content }) {
  const spacerRef = useRef(null);
  const imageRef = useRef(null);
  const { isMobile, isTablet } = useBreakpointContext();
  const [captionToShow, setCaptionToShow] = useState(null);
  const captions = (content.identify.captions || []).map(caption => ({
    title: caption.title,
    body: caption.body,
    zoom: Number(caption.zoom),
    x: Number(caption.x),
    y: Number(caption.y)
  }));

  const { imageWidth, imageHeight } = getImageDimensions(isMobile, isTablet);


  const headerHeight = isMobile || isTablet ? mobileHeaderHeight : tabletHeaderHeight;
  const stickyTop = 0.5 * (window.innerHeight - headerHeight - imageHeight) + headerHeight;

  // Scroll handler for zoom/pan animations
  useEffect(() => {
    const handleScroll = () => {
      if (!spacerRef.current || !imageRef.current) return;

      const progress = calculateScrollProgress(spacerRef.current);
      const indices = calculateCaptionIndices(progress, captions.length);
      setCaptionToShow(indices.displayCaption);
      const { scale, transformOrigin } = calculateImageTransform(progress, captions, indices);


      imageRef.current.style.transform = `scale(${scale})`;
      imageRef.current.style.transformOrigin = transformOrigin;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <section className='container inset identifying-cuttlefish no-border'>
        <SectionHeader title="How do you identify the dwarf cuttlefish?" topBorder />
        <div className='vstack'>
          <p className='narrow center padded'>
            {content.identify.intro}
          </p>
          <div className='image-wrapper'
            style={{
              position: 'sticky',
              top: `${stickyTop}px`,
              width: '100%',
              overflow: 'hidden',
              height: `${imageHeight}px`
            }}
          >
            <div 
              className='image-container' style={{ position: 'relative', height: '100%', width: '100%', overflow: 'hidden' }}>


              <img
                ref={imageRef}
                src={`assets/expeditionspage/identifying-cuttlefish.jpg`}
                alt='Identifying cuttlefish'
                className='identifying-cuttlefish-image'
                style={{
                  width: '100%',
                  transformOrigin: 'center',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />

              {/* Caption overlays - all rendered with opacity control */}
              {captions.map((caption, index) => (
                <div
                  key={index}
                  className='caption-overlay'
                  style={{
                    position: 'absolute',
                    top: '5%',
                    left: '3%',
                    width: '100%',
                    height: '100%',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'start',
                    justifyContent: 'flex-start',
                    opacity: captionToShow === index ? 1 : 0,
                    transition: 'opacity 0.2s ease-in-out',
                    pointerEvents: captionToShow === index ? 'auto' : 'none'
                  }}
                >
                  <div className='caption-content' style={{
                    background: 'black',
                    borderRadius: '15px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '24px',
                    maxWidth: '600px',
                    width: '90%',
                    marginBottom: '6%'
                  }}>
                    <img
                      src={`assets/expeditionspage/caption${index + 1}.png`}
                      alt="Caption illustration"
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      flex: 1
                    }}>
                      <h3 style={{
                        margin: 0,
                        fontWeight: 'bold',
                        color: 'white',
                        fontSize: '18px'
                      }}>
                        {caption.title}
                      </h3>
                      <p style={{
                        margin: 0,
                        color: 'white',
                        fontSize: '14px',
                        lineHeight: '1.4'
                      }}>
                        {caption.body}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 400vh spacer with red outline */}
          <div
            ref={spacerRef}
            style={{
              height: '400vh',
              // zIndex: 1000,
            }}
          ></div>

        </div>
      </section>

    </>
  )
}
